import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import communityAPI from "../../../api/community";
import { message, Popconfirm, Pagination, Collapse } from "antd";
import Avatar from "../../../componentes/ui/Avatar";
import { FullscreenSpinner, InlineSpinner } from '../../../componentes/LoadingIndicator';
import UnapprovalFeedbackModal from "../UnapprovalFeedbackModal";
import { formatDate } from "../../../utils/dateFormat";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import CategoryManager from "./CategoryManager";
import TagManager from "./TagManager";
import UserModerationPanel from "./UserModerationPanel";
import LegalDocumentManager from "./LegalDocumentManager";
import { formatNumber } from "../../../utils/numberFormat";
import {
  Hourglass,
  CheckCircle2,
  Layers,
  Clock3,
  ShieldCheck,
  Heart,
  MessageCircle,
  Eye,
} from "lucide-react";

const extractResults = (payload) =>
  Array.isArray(payload) ? payload : payload?.results || [];

const extractCount = (payload) => {
  if (!payload) {
    return 0;
  }
  if (Array.isArray(payload)) {
    return payload.length;
  }
  if (typeof payload.count === "number") {
    return payload.count;
  }
  if (Array.isArray(payload.results)) {
    return payload.results.length;
  }
  return 0;
};

export default function AdminDashboard({ embedded = false, onAction }) {
  const { isAdmin } = useCommunityAuth();
  const adminToolsRef = useRef(null);
  const [activeAdminPanel, setActiveAdminPanel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [typeCounts, setTypeCounts] = useState({
    all: 0,
    blog: 0,
    forum: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    post: null,
    mode: "unapprove",
  });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const pageSize = embedded ? 3 : 5;

  const buildQueryParams = useCallback(
    (page) => {
      const params = {
        page,
        page_size: pageSize,
      };

      if (statusFilter === "pending") {
        params.review_status = "pending_review";
      } else if (statusFilter === "approved") {
        params.review_status = "approved";
      } else if (statusFilter === "rejected") {
        params.review_status = "requires_revision";
      }

      if (typeFilter !== "all") {
        params.post_type = typeFilter;
      }

      return params;
    },
    [pageSize, statusFilter, typeFilter]
  );

  const fetchPosts = useCallback(
    async ({ page = 1, silent = false, allowFallback = true } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        const params = buildQueryParams(page);
        const response = await communityAPI.posts.getAll(params);
        const payload = response.data;
        const results = extractResults(payload);
        const total = extractCount(payload);

        if (results.length === 0 && total > 0 && page > 1 && allowFallback) {
          return fetchPosts({ page: page - 1, silent, allowFallback: false });
        }

        setPosts(results);
        setTotalCount(total);
        setCurrentPage(page);
        return { results, total };
      } catch (err) {
        console.error("Failed to load posts:", err);
        if (!silent) {
          message.error("Unable to load posts right now.");
        }
        throw err;
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [buildQueryParams]
  );

  const fetchCounts = useCallback(async () => {
    try {
      const [
        allRes,
        pendingRes,
        approvedRes,
        rejectedRes,
        blogRes,
        forumRes,
      ] =
        await Promise.all([
          communityAPI.posts.getAll({ page_size: 1 }),
          communityAPI.posts.getAll({ page_size: 1, review_status: "pending_review" }),
          communityAPI.posts.getAll({ page_size: 1, review_status: "approved" }),
          communityAPI.posts.getAll({ page_size: 1, review_status: "requires_revision" }),
          communityAPI.posts.getAll({ page_size: 1, post_type: "blog" }),
          communityAPI.posts.getAll({ page_size: 1, post_type: "forum" }),
        ]);

      const allData = allRes.data;
      const pendingData = pendingRes.data;
      const approvedData = approvedRes.data;
      const rejectedData = rejectedRes.data;
      const blogData = blogRes.data;
      const forumData = forumRes.data;

      setStatusCounts({
        all: extractCount(allData),
        pending: extractCount(pendingData),
        approved: extractCount(approvedData),
        rejected: extractCount(rejectedData),
      });

      setTypeCounts({
        all: extractCount(allData),
        blog: extractCount(blogData),
        forum: extractCount(forumData),
      });
    } catch (err) {
      console.error("Failed to load moderation summary:", err);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchPosts({ page: 1 });
  }, [fetchPosts]);

  const totalPosts = statusCounts.all || 0;
  const pendingCount = statusCounts.pending || 0;
  const approvedCount = statusCounts.approved || 0;
  const rejectedCount = statusCounts.rejected || 0;
  const blogCount = typeCounts.blog || 0;
  const forumCount = typeCounts.forum || 0;

  const approvalRate = useMemo(() => {
    if (!totalPosts) {
      return 0;
    }
    return Math.round((approvedCount / totalPosts) * 100);
  }, [approvedCount, totalPosts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter]);

  const statusFilters = [
    { key: "all", label: "All Posts", count: totalPosts },
    { key: "pending", label: "Pending", count: pendingCount },
    { key: "approved", label: "Approved", count: approvedCount },
    { key: "rejected", label: "Rejected", count: rejectedCount },
  ];

  const typeFilters = [
    { key: "all", label: "All Types", count: totalPosts },
    { key: "blog", label: "Blogs", count: blogCount },
    { key: "forum", label: "Forums", count: forumCount },
  ];

  const stats = useMemo(
    () => [
      {
        label: "Pending Review",
        value: formatNumber(pendingCount),
        description: "Awaiting decisions",
        // gradient: "from-amber-400 via-amber-500 to-orange-500",
        // textClass: "text-white",
        gradient:
          "from-slate-200 via-white to-slate-100 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900",
        textClass: "text-gray-900 dark:text-gray-100",
        iconWrap: "bg-white/10 text-white",
        icon: <Hourglass className="w-8 h-8" strokeWidth={1.5} />,
      },
      {
        label: "Approval Rate",
        value: `${approvalRate}%`,
        description: `${formatNumber(approvedCount)} of ${formatNumber(
          totalPosts || 0
        )} published`,
        // gradient: "from-indigo-500 via-purple-500 to-indigo-600",
        // textClass: "text-white",
        gradient:
          "from-slate-200 via-white to-slate-100 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900",
        textClass: "text-gray-900 dark:text-gray-100",
        iconWrap: "bg-white/10 text-white",
        icon: <CheckCircle2 className="w-8 h-8" strokeWidth={1.5} />,
      },
      {
        label: "Total Submissions",
        value: formatNumber(totalPosts),
        description: "Across all content types",
        gradient:
          "from-slate-200 via-white to-slate-100 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900",
        textClass: "text-gray-900 dark:text-gray-100",
        iconWrap:
          "bg-gray-900/5 dark:bg-white/10 text-gray-900 dark:text-white",
        icon: <Layers className="w-8 h-8" strokeWidth={1.5} />,
      },
    ],
    [pendingCount, approvalRate, approvedCount, totalPosts]
  );

  const handleApprove = async (slug) => {
    try {
      await communityAPI.posts.approve(slug);
      message.success("Post approved");
      await Promise.all([
        fetchPosts({ page: currentPage }),
        fetchCounts(),
      ]);
      if (onAction) onAction();
    } catch (err) {
      console.error("Failed to approve post:", err);
    }
  };

  const handleOpenUnapprove = (post) => {
    setFeedbackModal({ open: true, post, mode: "unapprove" });
  };

  const handleEditFeedback = (post) => {
    setFeedbackModal({ open: true, post, mode: "edit" });
  };

  const handleCloseUnapprove = () => {
    setFeedbackModal({ open: false, post: null, mode: "unapprove" });
  };

  const handleSubmitUnapprove = async (feedback) => {
    if (!feedbackModal.post) {
      return;
    }

    setSubmittingFeedback(true);
    try {
      const payload = {
        unapproval_message: feedback.message,
        unapproval_highlights: feedback.highlights,
      };

      if (feedbackModal.mode === "unapprove") {
        payload.is_approved = false;
      }

      await communityAPI.posts.update(feedbackModal.post.slug, payload);
      message.success(
        feedbackModal.mode === "edit"
          ? "Feedback updated for the author"
          : "Feedback shared with the author"
      );
      handleCloseUnapprove();
      await Promise.all([
        fetchPosts({ page: currentPage }),
        fetchCounts(),
      ]);
      if (onAction) onAction();
    } catch (err) {
      console.error("Failed to submit unapproval feedback:", err);
      const detail =
        err.response?.data?.unapproval_message ||
        err.response?.data?.unapproval_highlights ||
        err.response?.data?.detail ||
        "Unable to submit feedback";
      if (Array.isArray(detail)) {
        message.error(detail.join(" "));
      } else {
        message.error(detail);
      }
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleDelete = async (slug) => {
    try {
      await communityAPI.posts.delete(slug);
      message.success("Post deleted");
      await Promise.all([
        fetchPosts({ page: currentPage }),
        fetchCounts(),
      ]);
      if (onAction) onAction();
    } catch (err) {
      console.error("Failed to delete post:", err);
      message.error("Failed to delete post");
    }
  };

  const handleStatusUpdate = useCallback(() => {
    fetchCounts();
    fetchPosts({ page: currentPage, silent: true }).catch(() => undefined);
    if (onAction) onAction();
  }, [fetchCounts, fetchPosts, currentPage, onAction]);

  const adminPanels = useMemo(() => {
    if (!isAdmin || embedded) {
      return [];
    }
    return [
      {
        key: "members",
        label: (
          <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            Manage Members
          </span>
        ),
        children: (
          <div className="pt-4">
            <UserModerationPanel onStatusChange={handleStatusUpdate} />
          </div>
        ),
      },
      {
        key: "categories",
        label: (
          <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            Manage Categories
          </span>
        ),
        children: (
          <div className="pt-4">
            <CategoryManager showHeader={false} />
          </div>
        ),
      },
      {
        key: "tags",
        label: (
          <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            Manage Tags
          </span>
        ),
        children: (
          <div className="pt-4">
            <TagManager showHeader={false} />
          </div>
        ),
      },
      {
        key: "legal",
        label: (
          <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            Legal Documents
          </span>
        ),
        children: (
          <div className="pt-4">
            <LegalDocumentManager />
          </div>
        ),
      },
    ];
  }, [embedded, handleStatusUpdate, isAdmin]);

  const adminActionItems = useMemo(() => {
    if (!isAdmin || embedded) {
      return [];
    }

    return [
      {
        key: "members",
        label: "Manage Members",
        kind: "panel",
      },
      {
        key: "categories",
        label: "Manage Categories",
        kind: "panel",
      },
      {
        key: "tags",
        label: "Manage Tags",
        kind: "panel",
      },
      {
        key: "reports",
        label: "Review Comment Reports",
        kind: "link",
        to: "/admin/reports",
      },
      {
        key: "legal",
        label: "Legal Documents",
        kind: "panel",
      },
    ];
  }, [embedded, isAdmin]);

  const openAdminPanel = (key) => {
    setActiveAdminPanel(key);
    adminToolsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const stripImages = (html) => html?.replace(/<img[^>]*>/g, "") || "";

  const getContentPreview = (post) => {
    const raw = stripImages(post.excerpt || post.content || "");
    const doc = new DOMParser().parseFromString(raw, "text/html");
    const textOnly = (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    
    if (!textOnly) return "No preview available for this submission yet.";
    if (textOnly.length <= 180) return textOnly;
    return `${textOnly.slice(0, 177)}...`;
  };

  const badgeBaseClass =
    "inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-semibold tracking-wide whitespace-nowrap";

  const getStatusBadgeMeta = (status) => {
    switch (status) {
      case "approved":
        return {
          label: "Approved",
          className:
            "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:bg-emerald-400/15 dark:text-emerald-100 dark:border-emerald-400/25",
        };
      case "requires_revision":
        return {
          label: "Needs Revision",
          className:
            "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:bg-rose-400/15 dark:text-rose-100 dark:border-rose-400/25",
        };
      case "pending_review":
      default:
        return {
          label: "Pending Review",
          className:
            "bg-amber-500/10 text-amber-600 border-amber-500/30 dark:bg-amber-400/20 dark:text-amber-100 dark:border-amber-400/25",
        };
    }
  };

  const getTypeBadgeClass = (type) => {
    const palette = {
      blog: "bg-sky-500/10 text-sky-600 border-sky-500/30 dark:bg-sky-400/15 dark:text-sky-100 dark:border-sky-400/25",
      forum:
        "bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/30 dark:bg-fuchsia-400/15 dark:text-fuchsia-100 dark:border-fuchsia-400/25",
      learning:
        "bg-[#3db8d9]/10 text-primary border-primary/30 dark:bg-indigo-400/15 dark:text-indigo-100 dark:border-primary/60/25",
      challenges:
        "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:bg-rose-400/15 dark:text-rose-100 dark:border-rose-400/25",
    };
    return (
      palette[type?.toLowerCase()] ||
      "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:bg-blue-400/15 dark:text-blue-100 dark:border-blue-400/25"
    );
  };

  const categoryBadgeClass =
    "bg-purple-500/10 text-secondary border-purple-500/30 dark:bg-purple-400/15 dark:text-purple-100 dark:border-purple-400/25";

  const paginatedPosts = useMemo(() => posts, [posts]);

  if (loading) {
    if (embedded) {
      return (
        <div className="flex justify-center py-10">
          <InlineSpinner tone="slate" message="Loading moderation queue..." />
        </div>
      );
    }

    return (
      <div className="min-h-[50vh] bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <FullscreenSpinner message="Loading moderation queue..." tone="slate" />
      </div>
    );
  }

  const wrapperClass = embedded
    ? "space-y-4 p-4 md:p-6"
    : "relative max-w-7xl mx-auto px-4 py-6 md:py-8";
  const headerClass = embedded ? "mb-6" : "mb-6 md:mb-8";
  const containerClass = embedded
    ? "space-y-4"
    : "relative overflow-hidden bg-slate-100 dark:bg-gray-950 min-h-[60vh] py-8 md:py-12";

  return (
    <div className={containerClass}>
      {!embedded && (
        <div className="pointer-events-none absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_65%)]" />
      )}
      <div className={`${wrapperClass} space-y-6`}>
        <div className={`${headerClass} flex flex-col gap-6`}>
          {!embedded && (
            <div className="relative overflow-hidden rounded-3xl border border-primary/60/30 dark:border-primary/20 bg-gradient-to-br from-primary via-secondary to-blue-600 text-white shadow-2xl">
              <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.35),_transparent_60%)]" />
              <div className="relative p-4 md:p-10 flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-1 space-y-4 md:space-y-6">
                  <div className="inline-flex items-center gap-3 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/15 backdrop-blur text-xs md:text-sm font-semibold tracking-wide">
                    <span className="inline-flex h-2 w-2 rounded-full bg-emerald-300 animate-pulse" />
                    Community Quality Desk
                  </div>
                  <div>
                    <h1 className="text-xl md:text-3xl lg:text-4xl font-bold leading-tight">
                      Post Approval Center
                    </h1>
                    <p className="mt-2 md:mt-3 text-sm md:text-base lg:text-lg text-white/80 max-w-2xl">
                      Shape the community narrative by elevating the most
                      impactful stories, safeguarding trust, and keeping
                      discussions relevant for every learner.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm text-white/80">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-xl bg-white/15">
                        <Clock3
                          className="w-4 h-4 md:w-5 md:h-5"
                          strokeWidth={1.5}
                        />
                      </span>
                      <span>
                        Review new submissions within 24 hours to keep momentum
                        high.
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-xl bg-white/15">
                        <ShieldCheck
                          className="w-4 h-4 md:w-5 md:h-5"
                          strokeWidth={1.5}
                        />
                      </span>
                      <span>
                        Boost member credibility by curating thoughtful,
                        actionable content.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="w-full md:w-72 lg:w-80 flex-shrink-0 rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-4 md:p-6 space-y-3 md:space-y-4 self-stretch">
                  <h2 className="text-base md:text-lg font-semibold">
                    Daily Moderation Pulse
                  </h2>
                  <div className="space-y-2 md:space-y-3 text-xs md:text-sm text-white/70">
                    <div className="flex items-center justify-between">
                      <span>Items awaiting review</span>
                      <span className="text-white font-semibold">
                        {formatNumber(pendingCount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Publishing success rate</span>
                      <span className="text-white font-semibold">
                        {formatNumber(approvalRate)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Total submissions</span>
                      <span className="text-white font-semibold">
                        {formatNumber(totalPosts)}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-xl bg-black/20 px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-white/80 border border-white/10">
                    Stay responsive—authors receive instant feedback when a
                    decision is made.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.map(
              ({
                label,
                value,
                description,
                gradient,
                textClass,
                iconWrap,
                icon,
              }) => (
                <div
                  key={label}
                  className={`relative overflow-hidden rounded-2xl border border-white/20 dark:border-white/10 bg-gradient-to-br ${gradient} p-4 md:p-6 shadow-lg transition transform hover:-translate-y-1 hover:shadow-2xl`}
                >
                  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.6),_transparent_70%)]" />
                  <div className="relative flex items-start justify-between gap-4 md:gap-6">
                    <div className={`space-y-1 md:space-y-2 ${textClass}`}>
                      <p className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.35em] opacity-80">
                        {label}
                      </p>
                      <p className="text-2xl md:text-4xl font-bold">{value}</p>
                      <p className="text-xs md:text-sm opacity-80">
                        {description}
                      </p>
                    </div>
                    <span
                      className={`flex h-10 w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-xl ${iconWrap}`}
                    >
                      {icon}
                    </span>
                  </div>
                </div>
              )
            )}
          </div>

          {adminActionItems.length > 0 && (
            <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur border border-gray-200/70 dark:border-gray-800/70 rounded-2xl p-4 shadow-lg">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Admin Actions
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Manage members, content structure, and trust signals.
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {adminActionItems.map((item) => {
                  if (item.kind === "link") {
                    return (
                      <Link
                        key={item.key}
                        to={item.to}
                        className="inline-flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-950 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 transition hover:border-primary/40 hover:text-primary"
                      >
                        <span>{item.label}</span>
                        <span className="text-gray-400">→</span>
                      </Link>
                    );
                  }

                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => openAdminPanel(item.key)}
                      className="inline-flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-950 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 transition hover:border-primary/40 hover:text-primary"
                    >
                      <span>{item.label}</span>
                      <span className="text-gray-400">→</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {adminPanels.length > 0 && (
            <div ref={adminToolsRef}>
              <Collapse
                accordion
                bordered={false}
                activeKey={activeAdminPanel}
                onChange={(key) => setActiveAdminPanel(key)}
                items={adminPanels}
                className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/80 dark:bg-gray-900/80 backdrop-blur"
              />
            </div>
          )}

          {!embedded && (
            <div className="flex justify-end">
              <Link
                to="/admin/reports"
                className="inline-flex items-center gap-2 rounded-xl border border-primary/30/60 bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm transition hover:border-primary/60 hover:text-[#229bb8] dark:border-primary/20 dark:bg-gray-900 dark:text-indigo-200 dark:hover:border-primary/60"
              >
                Review Comment Reports
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          )}

          <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur border border-gray-200/70 dark:border-gray-800/70 rounded-2xl p-4 shadow-lg">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2 md:gap-3">
                {statusFilters.map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(key)}
                    className={`flex-1 sm:flex-none justify-center px-4 md:px-5 py-2 rounded-xl font-semibold transition flex items-center gap-2 text-sm md:text-base ${
                      statusFilter === key
                        ? "bg-primary text-white shadow-md shadow-indigo-500/30"
                        : "bg-transparent text-gray-700 dark:text-gray-200 hover:bg-white/80 dark:hover:bg-gray-800/80"
                    }`}
                  >
                    <span className="whitespace-nowrap">{label}</span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        statusFilter === key
                          ? "bg-white/20"
                          : "bg-gray-200/80 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {formatNumber(count)}
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 md:gap-3 lg:justify-end border-t lg:border-t-0 border-gray-200 dark:border-gray-700 pt-4 lg:pt-0">
                {typeFilters.map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setTypeFilter(key)}
                    className={`flex-1 sm:flex-none justify-center px-4 md:px-5 py-2 rounded-xl font-semibold transition flex items-center gap-2 text-sm md:text-base ${
                      typeFilter === key
                        ? "bg-primary/90 text-white shadow-md shadow-indigo-500/30"
                        : "bg-transparent text-gray-700 dark:text-gray-200 hover:bg-white/80 dark:hover:bg-gray-800/80"
                    }`}
                  >
                    <span className="whitespace-nowrap">{label}</span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        typeFilter === key
                          ? "bg-white/20"
                          : "bg-gray-200/80 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {formatNumber(count)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {paginatedPosts.length === 0 ? (
            <div className="text-center py-20 text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl bg-white/80 dark:bg-gray-900/40 backdrop-blur">
              No posts found for this filter
            </div>
          ) : (
            paginatedPosts.map((post) => {
              const statusMeta = getStatusBadgeMeta(post.review_status);
              return (
              <div
                key={post.id}
                className="group relative overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-700/70 bg-white dark:bg-gray-900 p-5 md:p-7 shadow-md transition transform hover:-translate-y-1 hover:shadow-2xl"
              >
                <div className="absolute inset-0 pointer-events-none opacity-10 bg-gradient-to-br from-indigo-400 via-transparent to-transparent group-hover:opacity-20" />
                <div className="relative flex flex-col lg:flex-row gap-6 lg:items-start lg:justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
                      <span
                        className={`${badgeBaseClass} uppercase ${statusMeta.className}`}
                      >
                        {statusMeta.label}
                      </span>
                      <span
                        className={`${badgeBaseClass} ${getTypeBadgeClass(
                          post.post_type
                        )} capitalize`}
                      >
                        {post.post_type}
                      </span>
                      {post.category && (
                        <span
                          className={`${badgeBaseClass} ${categoryBadgeClass} capitalize`}
                        >
                          {post.category.name}
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 text-sm md:text-base">
                      {getContentPreview(post)}
                    </p>

                    {post.review_status !== "approved" && post.review_feedback?.message && (
                      <div className="m-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-500/40 dark:bg-amber-400/10 dark:text-amber-100">
                        <p className="text-sm font-semibold mb-1">
                          Feedback shared with the author
                        </p>
                        <p className="text-sm whitespace-pre-line">
                          {post.review_feedback.message}
                        </p>
                        {post.review_feedback.updated_at && (
                          <p className="text-xs mt-2 text-amber-600 dark:text-amber-200">
                            Updated{" "}
                            {formatDate(post.review_feedback.updated_at)}
                          </p>
                        )}
                        {Array.isArray(post.review_feedback.highlights) &&
                          post.review_feedback.highlights.length > 0 && (
                            <ul className="mt-3 space-y-2 text-sm">
                              {post.review_feedback.highlights.map(
                                (item, index) => (
                                  <li
                                    key={`admin-feedback-${post.id}-${index}`}
                                    className="rounded-lg bg-white/60 dark:bg-amber-500/10 px-3 py-2"
                                  >
                                    <div className="font-medium text-amber-900 dark:text-amber-100">
                                      {item.excerpt || "Excerpt not provided"}
                                    </div>
                                    {item.note && (
                                      <div className="text-xs text-amber-700 dark:text-amber-200 mt-1">
                                        {item.note}
                                      </div>
                                    )}
                                  </li>
                                )
                              )}
                            </ul>
                          )}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                      <Link 
                        to={`/profile/${post.author?.username}`}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                      >
                        <Avatar
                          src={post.author?.avatar_url || post.author?.avatar}
                          name={post.author?.username || "Anonymous"}
                          size={8}
                          className="!text-xs"
                          isTrusted={post.author?.is_trusted || post.author?.status?.is_trusted}
                        />
                        <span className="font-medium">
                          {post.author?.username || "Anonymous"}
                        </span>
                      </Link>
                      <span className="hidden sm:inline">•</span>
                      <span>{formatDate(post.created_at)}</span>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" strokeWidth={2} />
                        {formatNumber(post.likes_count ?? 0)}{" "}
                        <span className="hidden sm:inline">Likes</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" strokeWidth={2} />
                        {formatNumber(post.comments_count ?? 0)}{" "}
                        <span className="hidden sm:inline">Replies</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" strokeWidth={2} />
                        {formatNumber(post.views ?? 0)}{" "}
                        <span className="hidden sm:inline">Views</span>
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row lg:flex-col gap-2 w-full lg:w-auto lg:min-w-[160px] pt-4 lg:pt-0 border-t lg:border-t-0 border-gray-100 dark:border-gray-800">
                    <Link
                      to={`/community/${post.post_type}/${post.slug}`}
                      state={{ fromAdmin: true }}
                      className="flex-1 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition text-center text-sm"
                    >
                      View
                    </Link>
                    {post.review_status !== "approved" ? (
                      <>
                        <button
                          onClick={() => handleApprove(post.slug)}
                          className="flex-1 px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition text-sm"
                        >
                          Approve
                        </button>
                        {post.review_feedback?.message && (
                          <button
                            onClick={() => handleEditFeedback(post)}
                            className="flex-1 px-4 py-2 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 transition text-sm"
                          >
                            Edit Feedback
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => handleOpenUnapprove(post)}
                        className="flex-1 px-4 py-2 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 transition text-sm"
                      >
                        Unapprove
                      </button>
                    )}
                    <Popconfirm
                      title="Delete this post?"
                      description="This action cannot be undone."
                      okText="Delete"
                      okType="danger"
                      onConfirm={() => handleDelete(post.slug)}
                      placement="topRight"
                    >
                      <button className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition text-sm">
                        Delete
                      </button>
                    </Popconfirm>
                  </div>
                </div>
              </div>
            );
            })
          )}
        </div>
        {totalCount > pageSize && (
          <div className="flex justify-center pt-6">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              showSizeChanger={false}
              onChange={(page) => fetchPosts({ page })}
              className="admin-pagination"
            />
          </div>
        )}
        <UnapprovalFeedbackModal
          open={feedbackModal.open}
          postTitle={feedbackModal.post?.title}
          initialFeedback={feedbackModal.post?.review_feedback}
          confirmLoading={submittingFeedback}
          onCancel={handleCloseUnapprove}
          onSubmit={handleSubmitUnapprove}
          modalTitle={
            feedbackModal.mode === "edit"
              ? "Update Revision Guidance"
              : "Provide Revision Guidance"
          }
          submitLabel={
            feedbackModal.mode === "edit"
              ? "Update Feedback"
              : "Submit Feedback"
          }
        />
      </div>
    </div>
  );
}

AdminDashboard.propTypes = {
  embedded: PropTypes.bool,
  onAction: PropTypes.func,
};
